import { FC } from 'react'

import { TextBadge } from 'ui/components/Badges'
import { TextButton } from 'ui/components/Buttons'
import { Icon, IconFace, IconName, IconSize } from 'ui/components/Icon'
import { LinearProgress } from 'ui/components/LinearProgress'

import { useUploadFileActions } from '../../../../hooks/useUploadFileActions'
import { UploadItem } from '../../../../types'

import { s } from './styles'

interface UploadFileItemProps {
  upload: UploadItem
}

export const UploadFileItem: FC<UploadFileItemProps> = ({ upload }) => {
  const { cancelUpload, retryUpload } = useUploadFileActions(upload.id)

  const renderIcon = () => {
    switch (upload.status) {
      case 'uploading':
        return (
          <s.SpinnerWrapper>
            <Icon name={IconName.SPINNER_ALT_ARROWS} size={IconSize.M} />
          </s.SpinnerWrapper>
        )
      case 'success':
        return <Icon name={IconName.CHECK} size={IconSize.M} face={IconFace.BLACK} />
      case 'error':
        return <Icon name={IconName.EXCLAMATION} size={IconSize.M} face={IconFace.LIGHT_DANGER} />
      case 'cancelled':
        return <Icon name={IconName.EXCLAMATION_ORANGE} size={IconSize.M} />
      default:
        return null
    }
  }

  const renderStatus = () => {
    switch (upload.status) {
      case 'uploading':
        return `Uploading`
      case 'success':
        if (upload.uploadedTo) {
          return (
            <>
              Uploaded to{' '}
              <s.UploadedLink href="#" onClick={(e) => e.preventDefault()}>
                {upload.uploadedTo}
              </s.UploadedLink>
            </>
          )
        }
        return 'Uploaded'
      case 'error':
        return 'Uploading Error'
      case 'cancelled':
        return 'Uploading Error'
      default:
        return ''
    }
  }

  const renderActions = () => {
    if (upload.status === 'uploading') {
      return (
        <s.FileActions>
          <TextButton onClick={cancelUpload}>Cancel</TextButton>
        </s.FileActions>
      )
    }
    if (upload.status === 'error' || upload.status === 'cancelled') {
      return (
        <s.FileActions>
          <TextButton onClick={retryUpload}>Repeat</TextButton>
        </s.FileActions>
      )
    }
    return null
  }

  const shouldShowProgressBar = upload.status === 'uploading' || (upload.status === 'success' && upload.isShrinking)

  return (
    <>
      <s.FileItemWrapper>
        <s.FileItem>
          <s.InfoWrapper>
            <s.IconWrapper>{renderIcon()}</s.IconWrapper>
            <s.FileInfo>
              <s.FileName>{upload.fileName}</s.FileName>
              <div>
                <TextBadge color="default">{upload.fileType}</TextBadge>
                <s.FileStatus>{renderStatus()}</s.FileStatus>
              </div>
            </s.FileInfo>
          </s.InfoWrapper>
          {renderActions()}
        </s.FileItem>
      </s.FileItemWrapper>
      {shouldShowProgressBar && (
        <s.ProgressBar $isShrinking={upload.isShrinking}>
          <LinearProgress value={upload.displayProgress} />
        </s.ProgressBar>
      )}
    </>
  )
}
